import React from 'react';
import type { PortCallEntry } from '@seaber/common/core/entries';
import type { Currency } from '@seaber/common/graphql/types';
import { sortBy, sum, groupBy, sumBy } from 'lodash';
import { useState } from 'react';
import { formatCurrency } from '../../../lib/format';
import type { PnlItem } from '../../pnl/pnl';
import { TableRow, TableCell, Text, Tooltip } from '../../ui';
import type { CostParam, Cost } from '../costs/costs';
import { ParamIndicator } from './ParamIndicator';

export interface SummaryRowProps {
  currency: Currency | null;
  depth?: number;
  errors?: string[];
  externalValue?: number | null;
  initialExpanded?: boolean;
  isBold?: boolean;
  isHiddenIfNull?: boolean;
  name: string;
  params?: CostParam[];
  subRows?: SummaryRowProps[];
  value: number;
  valueFormatFn?: (value: number) => string;
}

export function SummaryRow(props: SummaryRowProps): JSX.Element | null {
  const isExpandable = props.subRows !== undefined && props.subRows.length > 0;
  const [isExpanded, setIsExpanded] = useState(props.initialExpanded ?? false);

  const isVisible = props.isHiddenIfNull
    ? !!props.value || !!props.externalValue
    : true;

  function formatValue(value: number): string {
    if (props.valueFormatFn) {
      return props.valueFormatFn(value);
    }

    return (
      formatCurrency(value, 0, props.currency, {
        signDisplay: 'negative',
      }) ?? ''
    );
  }

  const Errors = () =>
    props.errors && props.errors.length > 0 ? (
      <Tooltip
        content={
          <div>
            {props.errors.map((err) => (
              <p key={err}>{err}</p>
            ))}
          </div>
        }
      >
        <Text color="status.error">*</Text>
      </Tooltip>
    ) : null;

  return isVisible ? (
    <>
      <TableRow
        isMuted={props.isBold}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <TableCell>
          <Text
            fontWeight={props.isBold ? 'bold' : 'normal'}
            ml={(props.depth ?? 0) * 16}
          >
            {props.name}
          </Text>
          <Errors />
          {isExpandable && <Text ml={2}>{isExpanded ? '▼' : '►'}</Text>}
        </TableCell>
        <TableCell data-testid={`summary-${props.name}`}>
          <Text
            fontWeight={props.isBold ? 'bold' : 'normal'}
            ml={(props.depth ?? 0) * 16}
          >
            {formatValue(props.value)}
          </Text>
        </TableCell>
        {props.externalValue !== undefined && (
          <>
            <TableCell>
              <Text fontWeight={props.isBold ? 'bold' : 'normal'}>
                {props.externalValue !== null
                  ? formatValue(props.externalValue)
                  : null}
              </Text>
            </TableCell>
            <TableCell>
              <Text fontWeight={props.isBold ? 'bold' : 'normal'}>
                {props.externalValue !== null
                  ? formatCurrency(
                      props.value - props.externalValue,
                      0,
                      props.currency,
                      {
                        signDisplay: 'exceptZero',
                      },
                    )
                  : null}
              </Text>
            </TableCell>
          </>
        )}
        <TableCell>
          {props.params?.map((param) => (
            <ParamIndicator key={param.name} param={param} />
          ))}
        </TableCell>
      </TableRow>
      {isExpandable && isExpanded && (
        <>
          {props.subRows?.map((subRow) => (
            <SummaryRow
              key={subRow.name}
              {...subRow}
              depth={(props.depth ?? 0) + 1}
              initialExpanded={props.subRows?.length === 1}
            />
          ))}
        </>
      )}
    </>
  ) : null;
}

export function createCostSubRows(
  costsByPortCallId: Map<string | null, Cost[]>,
  costType: 'PORT_CALL' | 'BUNKER' | 'CHANNEL',
  baseCurrency: Currency | null,
  portCallEntriesByPortClientId: Map<string, PortCallEntry>,
): SummaryRowProps[] {
  return sortBy(
    [...costsByPortCallId],
    ([portCallId]) =>
      (portCallId &&
        portCallEntriesByPortClientId.get(portCallId)?.plannedArrival) ??
      Infinity,
  ).map(([_portCallId, costs]): SummaryRowProps => {
    const filteredCosts = costs.filter((c) => c.type === costType);
    return {
      name: filteredCosts.at(0)?.locations.at(0)?.name ?? 'Other',
      value: sum(filteredCosts.map((cost) => cost.value) ?? []),
      currency: baseCurrency ?? null,
      subRows: filteredCosts.map((cost) => ({
        name: cost.name,
        value: cost.value,
        currency: baseCurrency ?? null,
      })),
    };
  });
}

/** Create sub row sturcture based on new pnl results */
export function createPnlSubRows<T extends PnlItem>(
  items: T[],
  opts: {
    baseCurrency?: Currency;
    /** Returns the grouping key for item */
    keyFn: (item: T) => string;
    labelFn?: (items: T[]) => string;
    paramFn?: (items: T[]) => CostParam[];
    showExternalValues?: boolean;
    valueFn?: (item: T) => number;
    valueFormatFn?: (value: number) => string;
  },
): SummaryRowProps[] {
  const rows: SummaryRowProps[] = [];
  const itemsByKey = groupBy(items, opts.keyFn);

  for (const [key, keyItems] of Object.entries(itemsByKey)) {
    const label = opts.labelFn?.(keyItems) ?? key;

    const total = sumBy(keyItems, (item) =>
      opts.valueFn ? opts.valueFn(item) : item.amount,
    );

    rows.push({
      name: label,
      value: total,
      currency: opts.baseCurrency ?? null,
      subRows: [],
      params: opts.paramFn ? opts.paramFn(keyItems) : [],
      externalValue: opts.showExternalValues ? null : undefined,
      valueFormatFn: opts.valueFormatFn,
    });
  }

  return rows;
}
